import React, { useState, useMemo, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Loader2, Search, RefreshCw, Settings, Info, Eye, EyeOff, CheckCircle2, XCircle, AlertCircle } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

interface Client {
  id: string
  client_id: number
  name: string
  cross_channel_orchestrator_enabled: boolean | null
  smartlead_api_key: string | null
  heyreach_api_key: string | null
}

export function CrossChannelOrchestratorTab() {
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'enabled' | 'disabled'>('all')
  const [editingClient, setEditingClient] = useState<string | null>(null)
  const [editingData, setEditingData] = useState<{
    cross_channel_orchestrator_enabled: boolean
    smartlead_api_key: string
    heyreach_api_key: string
  } | null>(null)
  const [showApiKeys, setShowApiKeys] = useState<Record<string, { smartlead: boolean; heyreach: boolean }>>({})
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    fetchClients()
  }, [])

  const fetchClients = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await supabase
        .from('clients')
        .select('id, client_id, name, status, cross_channel_orchestrator_enabled, smartlead_api_key, heyreach_api_key')
        .eq('status', 'Active') // Only show Active clients, matching Clients section
        .neq('id', 'default') // Exclude system default client
        .not('name', 'ilike', '%global%default%keys%') // Exclude Global Default Keys
        .not('name', 'ilike', '%default%keys%') // Extra filter for safety
        .not('name', 'ilike', '%outboundbuddy%') // Exclude Outboundbuddy
        .order('name')

      if (fetchError) throw fetchError

      setClients(data || [])
    } catch (err: any) {
      console.error('Error fetching clients:', err)
      setError(err.message || 'Failed to load clients')
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredClients = useMemo(() => {
    let filtered = clients

    // Apply status filter
    if (filterStatus === 'enabled') {
      filtered = filtered.filter(c => c.cross_channel_orchestrator_enabled === true)
    } else if (filterStatus === 'disabled') {
      filtered = filtered.filter(c => c.cross_channel_orchestrator_enabled === false || c.cross_channel_orchestrator_enabled === null)
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(search) ||
        c.client_id.toString().includes(search)
      )
    }

    return filtered
  }, [clients, filterStatus, searchTerm])

  const enabledCount = clients.filter(c => c.cross_channel_orchestrator_enabled === true).length

  const handleEdit = (client: Client) => {
    setEditingClient(client.id)
    setEditingData({
      cross_channel_orchestrator_enabled: client.cross_channel_orchestrator_enabled ?? false,
      smartlead_api_key: client.smartlead_api_key || '',
      heyreach_api_key: client.heyreach_api_key || ''
    })
  }

  const handleCancel = () => {
    setEditingClient(null)
    setEditingData(null)
  }

  const handleSave = async (clientId: string) => {
    if (!editingData) return

    setSaving(clientId)
    try {
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          cross_channel_orchestrator_enabled: editingData.cross_channel_orchestrator_enabled,
          smartlead_api_key: editingData.smartlead_api_key || null,
          heyreach_api_key: editingData.heyreach_api_key || null
        })
        .eq('id', clientId)

      if (updateError) throw updateError

      toast({
        title: 'Success',
        description: 'Cross-channel orchestrator settings updated'
      })

      await fetchClients()
      setEditingClient(null)
      setEditingData(null)
    } catch (err: any) {
      console.error('Error updating client:', err)
      toast({
        title: 'Error',
        description: err.message || 'Failed to update settings',
        variant: 'destructive'
      })
    } finally {
      setSaving(null)
    }
  }

  const toggleApiKeyVisibility = (clientId: string, type: 'smartlead' | 'heyreach') => {
    setShowApiKeys(prev => ({
      ...prev,
      [clientId]: {
        ...prev[clientId],
        [type]: !prev[clientId]?.[type]
      }
    }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading cross-channel orchestrator settings...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error Loading Settings</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Cross-Channel Orchestrator Settings
              </CardTitle>
              <CardDescription className="mt-2">
                Automatically remove leads from other channels when they reply positively or negatively
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchClients}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{clients.length}</div>
              <div className="text-sm text-muted-foreground">Total Clients</div>
            </div>
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <div className="text-2xl font-bold text-green-600 dark:text-green-500">{enabledCount}</div>
              <div className="text-sm text-muted-foreground">Orchestrator Enabled</div>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-500">{clients.length - enabledCount}</div>
              <div className="text-sm text-muted-foreground">Orchestrator Disabled</div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Info Alert */}
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertTitle>How It Works</AlertTitle>
            <AlertDescription className="text-sm mt-2">
              When a lead replies positively or negatively on Email (Smartlead), they are automatically removed from 
              LinkedIn campaigns (HeyReach), and vice versa. This only happens for first replies (reply_count = 1) 
              and requires both API keys to be configured.
            </AlertDescription>
          </Alert>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="flex h-10 w-[180px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="all">All Clients ({clients.length})</option>
              <option value="enabled">Enabled ({enabledCount})</option>
              <option value="disabled">Disabled ({clients.length - enabledCount})</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Client Cards Grid */}
      {filteredClients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-[200px] text-center">
            <Settings className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-lg font-medium">No clients found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {searchTerm ? 'Try a different search term' : 'No clients with this filter'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredClients.map(client => {
            const isEditing = editingClient === client.id
            const clientData = isEditing && editingData ? editingData : {
              cross_channel_orchestrator_enabled: client.cross_channel_orchestrator_enabled ?? false,
              smartlead_api_key: client.smartlead_api_key || '',
              heyreach_api_key: client.heyreach_api_key || ''
            }
            const showKeys = showApiKeys[client.id] || { smartlead: false, heyreach: false }

            return (
              <Card key={client.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <CardDescription className="mt-1">Client ID: {client.client_id}</CardDescription>
                    </div>
                    <Badge variant={client.cross_channel_orchestrator_enabled ? 'default' : 'secondary'}>
                      {client.cross_channel_orchestrator_enabled ? (
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                      ) : (
                        <XCircle className="h-3 w-3 mr-1" />
                      )}
                      {client.cross_channel_orchestrator_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`enable-${client.id}`} className="flex-1">
                      Enable Orchestrator
                    </Label>
                    {isEditing ? (
                      <Switch
                        id={`enable-${client.id}`}
                        checked={clientData.cross_channel_orchestrator_enabled}
                        onCheckedChange={(checked) => setEditingData(prev => prev ? { ...prev, cross_channel_orchestrator_enabled: checked } : null)}
                      />
                    ) : (
                      <Switch
                        id={`enable-${client.id}`}
                        checked={client.cross_channel_orchestrator_enabled ?? false}
                        disabled
                      />
                    )}
                  </div>

                  {/* Smartlead API Key */}
                  <div className="space-y-2">
                    <Label htmlFor={`smartlead-${client.id}`}>Smartlead API Key</Label>
                    {isEditing ? (
                      <div className="relative">
                        <Input
                          id={`smartlead-${client.id}`}
                          type={showKeys.smartlead ? 'text' : 'password'}
                          value={clientData.smartlead_api_key}
                          onChange={(e) => setEditingData(prev => prev ? { ...prev, smartlead_api_key: e.target.value } : null)}
                          placeholder="Enter Smartlead API key"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                          onClick={() => toggleApiKeyVisibility(client.id, 'smartlead')}
                        >
                          {showKeys.smartlead ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id={`smartlead-${client.id}`}
                          type={showKeys.smartlead ? 'text' : 'password'}
                          value={client.smartlead_api_key || ''}
                          disabled
                          placeholder="Not configured"
                        />
                        {client.smartlead_api_key && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id, 'smartlead')}
                          >
                            {showKeys.smartlead ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* HeyReach API Key */}
                  <div className="space-y-2">
                    <Label htmlFor={`heyreach-${client.id}`}>HeyReach API Key</Label>
                    {isEditing ? (
                      <div className="relative">
                        <Input
                          id={`heyreach-${client.id}`}
                          type={showKeys.heyreach ? 'text' : 'password'}
                          value={clientData.heyreach_api_key}
                          onChange={(e) => setEditingData(prev => prev ? { ...prev, heyreach_api_key: e.target.value } : null)}
                          placeholder="Enter HeyReach API key"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                          onClick={() => toggleApiKeyVisibility(client.id, 'heyreach')}
                        >
                          {showKeys.heyreach ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    ) : (
                      <div className="relative">
                        <Input
                          id={`heyreach-${client.id}`}
                          type={showKeys.heyreach ? 'text' : 'password'}
                          value={client.heyreach_api_key || ''}
                          disabled
                          placeholder="Not configured"
                        />
                        {client.heyreach_api_key && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                            onClick={() => toggleApiKeyVisibility(client.id, 'heyreach')}
                          >
                            {showKeys.heyreach ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {isEditing ? (
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        onClick={() => handleSave(client.id)}
                        disabled={saving === client.id}
                        className="flex-1"
                      >
                        {saving === client.id ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={saving === client.id}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(client)}
                      className="w-full"
                    >
                      Edit Settings
                    </Button>
                  )}

                  {/* Status Warnings */}
                  {client.cross_channel_orchestrator_enabled && (
                    <div className="space-y-1 pt-2">
                      {!client.smartlead_api_key && (
                        <Alert variant="destructive" className="py-2">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            Smartlead API key required
                          </AlertDescription>
                        </Alert>
                      )}
                      {!client.heyreach_api_key && (
                        <Alert variant="destructive" className="py-2">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            HeyReach API key required
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
